import { FiAward } from 'react-icons/fi';

export const bandDescriptorsData = {
  courseId: 'writing-band-descriptors',
  courseType: 'writing-course',
  title: 'Band Descriptors',
  icon: FiAward,
  overview: {
    impact: 'Understand exactly how you\'re scored',
    focus: 'IELTS Writing assessment criteria',
    uses: 'Improving your writing strategically',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'The Four Scoring Criteria',
      content: `<p>Your writing is assessed on four criteria, each worth 25%:</p>`,
      table: {
        headers: ['Criterion', 'What It Measures'],
        rows: [
          ['Task Achievement/Response', 'Did you answer the question fully?'],
          ['Coherence & Cohesion', 'Is your writing organized and linked?'],
          ['Lexical Resource', 'Is your vocabulary varied and accurate?'],
          ['Grammatical Range & Accuracy', 'Are your sentences varied and correct?'],
        ],
      },
    },
    {
      title: 'Task Achievement (TA)',
      content: `<p>To score well on Task Achievement:</p>`,
      points: [
        'Answer ALL parts of the question',
        'Present a clear position/overview throughout',
        'Develop ideas with relevant examples',
        'For Task 1: Include an overview and specific data',
      ],
      table: {
        headers: ['Band', 'Description'],
        rows: [
          ['7', 'All parts addressed; clear position; ideas well developed'],
          ['6', 'All parts addressed; position is clear; ideas are extended but not fully'],
          ['5', 'Partly addresses the task; position unclear at times'],
        ],
      },
    },
    {
      title: 'Lexical Resource (LR)',
      content: `<p>To score well on Vocabulary:</p>`,
      points: [
        'Use a wide range of vocabulary',
        'Use less common words appropriately',
        'Make few spelling errors',
        'Use collocations naturally',
      ],
      examples: [
        { label: 'Band 5', text: 'The problem is very big and very bad.' },
        { label: 'Band 7', text: 'The issue is particularly significant and poses considerable challenges.' },
      ],
    },
    {
      title: 'Grammatical Range & Accuracy (GRA)',
      content: `<p>To score well on Grammar:</p>`,
      points: [
        'Use a variety of sentence structures (simple, compound, complex)',
        'Make few grammatical errors',
        'Use complex structures accurately',
        'Control punctuation well',
      ],
      table: {
        headers: ['Band', 'Description'],
        rows: [
          ['7', 'Good range of structures; mostly error-free; good control'],
          ['6', 'Mix of structures; some errors that don\'t impede communication'],
          ['5', 'Limited range; frequent errors; meaning sometimes unclear'],
        ],
      },
    },
  ],
  exercises: [
    {
      title: 'Identify Scoring Issues',
      type: 'select',
      instructions: 'Which criterion is the main weakness in each sample?',
      questions: [
        {
          text: 'Essay only discusses one side when the question asks to "discuss both views."',
          options: ['Task Achievement', 'Coherence', 'Vocabulary', 'Grammar'],
          answer: 'Task Achievement',
          explanation: 'Not answering all parts of the question hurts TA.',
        },
        {
          text: 'Essay uses "good" and "bad" repeatedly instead of varied vocabulary.',
          options: ['Lexical Resource', 'Task Achievement', 'Grammar', 'Coherence'],
          answer: 'Lexical Resource',
          explanation: 'Limited vocabulary range affects the LR score.',
        },
        {
          text: 'Essay jumps from topic to topic without clear connections.',
          options: ['Coherence & Cohesion', 'Task Achievement', 'Vocabulary', 'Grammar'],
          answer: 'Coherence & Cohesion',
          explanation: 'Lack of logical flow and linking affects CC.',
        },
        {
          text: 'Essay only uses simple sentences: subject + verb + object.',
          options: ['Grammatical Range', 'Coherence', 'Vocabulary', 'Task Achievement'],
          answer: 'Grammatical Range',
          explanation: 'Limited sentence variety affects GRA.',
        },
        {
          text: 'Essay has many spelling errors like "goverment" and "enviroment."',
          options: ['Lexical Resource', 'Grammar', 'Coherence', 'Task Achievement'],
          answer: 'Lexical Resource',
          explanation: 'Spelling accuracy is part of Lexical Resource.',
        },
        {
          text: 'Task 1 essay has no overview paragraph.',
          options: ['Task Achievement', 'Coherence', 'Vocabulary', 'Grammar'],
          answer: 'Task Achievement',
          explanation: 'Missing overview seriously affects TA in Task 1.',
        },
      ],
    },
    {
      title: 'Match Band Level to Description',
      type: 'select',
      instructions: 'Which band level does each description match?',
      questions: [
        {
          text: 'The essay fully addresses all parts of the task with a clear position throughout.',
          options: ['Band 7', 'Band 5', 'Band 6'],
          answer: 'Band 7',
          explanation: 'Band 7 = all parts addressed, clear position, well-developed ideas.',
        },
        {
          text: 'The essay partly addresses the task; the position is unclear at times.',
          options: ['Band 5', 'Band 7', 'Band 6'],
          answer: 'Band 5',
          explanation: 'Band 5 = partly addresses task, unclear position.',
        },
        {
          text: 'Uses a wide range of vocabulary with occasional errors that do not reduce communication.',
          options: ['Band 7', 'Band 5', 'Band 4'],
          answer: 'Band 7',
          explanation: 'Band 7 = wide range, some less common items, occasional errors.',
        },
      ],
    },
    {
      title: 'Improve the Vocabulary',
      type: 'fill-in',
      instructions: 'Replace the basic word with a more academic alternative.',
      questions: [
        {
          text: '"The problem is very big." Replace "big" with ___',
          answer: 'significant',
          explanation: '"Significant" or "considerable" is more academic.',
        },
        {
          text: '"Technology is good for education." Replace "good" with ___',
          answer: 'beneficial',
          explanation: '"Beneficial" is more sophisticated.',
        },
        {
          text: '"Pollution has bad effects." Replace "bad" with ___',
          answer: 'detrimental',
          explanation: '"Detrimental" or "adverse" is more academic.',
        },
      ],
    },
    {
      title: 'Assess Sample Sentences',
      type: 'select',
      instructions: 'What band level does this sentence suggest for Grammar?',
      questions: [
        {
          text: '"Technology is good. It helps people. Many people use it."',
          options: ['Band 5', 'Band 7', 'Band 8'],
          answer: 'Band 5',
          explanation: 'Only simple sentences—limited grammatical range.',
        },
        {
          text: '"Although technology has numerous benefits, it can also create significant challenges that need to be addressed."',
          options: ['Band 7+', 'Band 5', 'Band 4'],
          answer: 'Band 7+',
          explanation: 'Complex sentence with variety and accuracy.',
        },
      ],
    },
    {
      title: 'Identify Improvements',
      type: 'writing',
      instructions: 'How would you improve these sentences to score higher?',
      questions: [
        {
          text: '"Lots of people think technology is good."',
          example: 'Numerous individuals believe that technology is beneficial for society.',
          tip: 'Replace informal "lots of" with "numerous," "good" with "beneficial."',
        },
        {
          text: '"The graph shows the number went up then down."',
          example: 'The graph illustrates that the figure increased steadily before experiencing a gradual decline.',
          tip: 'Use academic verbs (illustrates) and precise trend language.',
        },
      ],
    },
  ],
  tips: [
    'Read the official band descriptors on the IELTS website',
    'Get feedback that references specific criteria',
    'Focus on your weakest criterion first',
    'Practice essays and self-assess using the descriptors',
  ],
  dailyPractice: [
    'Assess your own writing against each criterion',
    'Compare your essays to Band 7+ model answers',
    'Practice addressing ALL parts of questions',
    'Focus on one criterion per practice session',
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'IELTS Band Descriptors: Understanding the four scoring criteria (TA, CC, LR, GRA) and how to improve each for higher band scores.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...bandDescriptorsData,
      title: bandDescriptorsData.title + ' (Foundations)',
      overview: {
        ...bandDescriptorsData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: bandDescriptorsData.sections.slice(0, Math.max(2, Math.ceil(bandDescriptorsData.sections.length / 2))),
      exercises: bandDescriptorsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...bandDescriptorsData,
      title: bandDescriptorsData.title + ' (Advanced)',
      overview: {
        ...bandDescriptorsData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: bandDescriptorsData.sections,
      exercises: bandDescriptorsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(bandDescriptorsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return bandDescriptorsData;
}
